import type { Experiment } from 'types';
import { useState } from 'react';
import * as Icons from 'lucide-react';
import { cn } from 'helpers/cn';
import { formatDate } from 'helpers/formatters';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  barGeometry,
  devicesOf,
  incompatibleReason,
  overlapWindow,
  sharedDevices,
  timelineSpan,
  type DeviceType,
  type IncompatibleReason,
} from 'helpers/experiment-overlap';

// Namespace import: see the note in menu-links.tsx: named lucide imports can
// resolve to undefined through the dev bundler's HMR namespace.
const { Check, Plus, CalendarX2, MonitorX, Monitor, Smartphone, Tablet } = Icons;

type ExperimentSelectorProps = {
  availableExperiments: Experiment[];
  experimentIds: number[];
  addExperiment: (id: number) => void;
  removeExperiment: (id: number) => void;
  /** Names the panel for the product tour, as its `data-tour` anchor. */
  tourAnchor?: string;
};

const DEVICE_ICON: Record<DeviceType, typeof Monitor> = {
  All: Monitor,
  Desktop: Monitor,
  Mobile: Smartphone,
  Tablet: Tablet,
};

const REASON_COPY: Record<IncompatibleReason, { label: string; icon: typeof Monitor }> = {
  dates: { label: 'No date overlap', icon: CalendarX2 },
  devices: { label: 'No device overlap', icon: MonitorX },
};

// Same tones the experiments library gives these statuses, so the same word never
// changes colour between screens. Anything unmapped falls back to the hairline chip.
const STATUS_TONE: Record<string, string> = {
  Running: 'border-ok-line text-ok',
  Deployed: 'border-accent-line text-accent',
};

/** A disclosure that is a sentence, not a button: quiet, but still obviously clickable. */
const DISCLOSURE = 'supporting hover:text-foreground text-left underline decoration-dotted underline-offset-4';

const runLabel = (experiment: Experiment) => {
  const start = experiment.startsAt ? formatDate(experiment.startsAt) : '-';
  const end = experiment.endsAt ? formatDate(experiment.endsAt) : 'ongoing';
  return `${start} → ${end}`;
};

export const ExperimentSelector = ({
  availableExperiments,
  experimentIds,
  addExperiment,
  removeExperiment,
  tourAnchor,
}: ExperimentSelectorProps) => {
  const [showIncompatible, setShowIncompatible] = useState(false);
  const now = Date.now();
  const selected = availableExperiments.filter((e) => experimentIds.includes(e.id));

  // Once something is selected, an experiment that shares no run window or
  // device with it cannot form a combination, so it is not offered. Kept
  // behind a disclosure rather than dropped silently, so the list does not
  // appear to lose entries for no reason.
  const annotated = availableExperiments.map((experiment) => {
    const isSelected = experimentIds.includes(experiment.id);
    return { experiment, isSelected, reason: isSelected ? null : incompatibleReason(experiment, selected) };
  });
  const hiddenCount = annotated.filter((a) => a.reason && !showIncompatible).length;
  const visible = showIncompatible ? annotated : annotated.filter((a) => !a.reason);

  const sharedWindow = overlapWindow(selected);
  const shared = sharedDevices(selected);
  const span = timelineSpan(selected.length > 1 ? selected : availableExperiments.slice(0, 12), now);

  return (
    <TooltipProvider>
      <section className="bg-panel border-line rounded-panel border" data-tour={tourAnchor}>
        {/*
          One line, like every other panel header in the console. This used to be a
          second title-and-paragraph block sitting directly under the page's own
          title-and-paragraph — two headers of near-identical weight stacked. The
          eligibility rule now lives in the footer, next to the prompt it explains.
        */}
        <div className="border-line flex items-center justify-between gap-2 border-b px-3 py-2.5">
          <h2 className="micro-label m-0">Select experiments to combine</h2>
          <span className="micro-label tracking-normal">{experimentIds.length} selected</span>
        </div>

        <div className="space-y-3 p-3">
          {selected.length > 1 && (
            <OverlapTimeline selected={selected} span={span} now={now} sharedWindow={sharedWindow} shared={shared} />
          )}

          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
            {visible.map(({ experiment, isSelected, reason }) => {
              const blocked = !!reason;

              return (
                <ExperimentCard
                  key={experiment.id}
                  experiment={experiment}
                  isSelected={isSelected}
                  reason={reason}
                  blocked={blocked}
                  onToggle={() => (isSelected ? removeExperiment(experiment.id) : addExperiment(experiment.id))}
                />
              );
            })}
          </div>

          {hiddenCount > 0 && (
            <button type="button" onClick={() => setShowIncompatible(true)} className={DISCLOSURE}>
              {hiddenCount} {hiddenCount === 1 ? 'experiment' : 'experiments'} hidden: no overlapping dates or devices
              with your selection. Show anyway
            </button>
          )}
          {showIncompatible && (
            <button type="button" onClick={() => setShowIncompatible(false)} className={DISCLOSURE}>
              Hide experiments that cannot be combined
            </button>
          )}

          {availableExperiments.length === 0 && (
            <p className="supporting px-1 py-6 text-center">No running or ended experiments on this website yet.</p>
          )}
        </div>

        {availableExperiments.length > 0 && (
          <div className="border-line border-t px-3 py-2.5">
            <p className="supporting m-0">
              {experimentIds.length < 2 && (
                <span className="text-foreground font-medium">Select at least two experiments. </span>
              )}
              Only experiments that ran at the same time, on at least one shared device type, can be combined — a
              combination only exists for traffic that was eligible for all of them.
            </p>
          </div>
        )}
      </section>
    </TooltipProvider>
  );
};

type ExperimentCardProps = {
  experiment: Experiment;
  isSelected: boolean;
  reason: IncompatibleReason | null;
  blocked: boolean;
  onToggle: () => void;
};

const ExperimentCard = ({ experiment, isSelected, reason, blocked, onToggle }: ExperimentCardProps) => {
  const devices = devicesOf(experiment);
  const StateIcon = isSelected ? Check : reason ? REASON_COPY[reason].icon : Plus;

  // State is carried by the icon and the text, never by colour alone.
  const stateLabel = isSelected ? 'Selected' : reason ? REASON_COPY[reason].label : 'Add';

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={blocked && !isSelected}
      aria-pressed={isSelected}
      className={cn(
        'group border-line bg-panel2 rounded-card flex w-full flex-col gap-2 border p-2.5 text-left transition-colors duration-150',
        isSelected
          ? 'border-sel-line bg-sel-bg'
          : blocked
            ? 'cursor-not-allowed border-dashed opacity-55'
            : 'hover:border-sel-line hover:bg-hover',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="line-clamp-2 text-body font-medium">{experiment.name}</span>
        <span
          className={cn(
            'rounded-pill mt-0.5 grid size-5 shrink-0 place-items-center border',
            isSelected ? 'border-sel-line bg-sel text-white' : 'border-line text-dim',
          )}
        >
          <StateIcon className="size-3" strokeWidth={1.5} />
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <span
          className={cn(
            'micro-label rounded-pill border px-2 py-0.5',
            STATUS_TONE[experiment.status] ?? 'border-line text-dim',
          )}
        >
          {experiment.status}
        </span>
        <span className="supporting">{runLabel(experiment)}</span>
      </div>

      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1">
          {devices.map((device) => {
            const DeviceIcon = DEVICE_ICON[device];
            return (
              <Tooltip key={device}>
                <TooltipTrigger asChild>
                  <DeviceIcon className="text-dim size-3.5" strokeWidth={1.5} aria-label={device} />
                </TooltipTrigger>
                <TooltipContent>{device}</TooltipContent>
              </Tooltip>
            );
          })}
          <span className="micro-label ml-1">{experiment.variants?.length ?? 0} variants</span>
        </span>
        <span className={cn('micro-label', isSelected && 'text-sel')}>{stateLabel}</span>
      </div>
    </button>
  );
};

type OverlapTimelineProps = {
  selected: Experiment[];
  span: { min: number; max: number };
  now: number;
  sharedWindow: { start: number; end: number | null } | null;
  shared: DeviceType[];
};

/**
 * Gantt-style bars on one shared time axis. The question is "did these run at
 * the same time", which is an interval comparison, so the bars share an axis and
 * the common window is banded behind them. One hue for every bar: a bar encodes
 * a run window, not a category, so no categorical palette is involved.
 */
const OverlapTimeline = ({ selected, span, now, sharedWindow, shared }: OverlapTimelineProps) => {
  const total = span.max - span.min || 1;
  const pct = (ms: number) => ((ms - span.min) / total) * 100;

  const bandStart = sharedWindow ? Math.max(pct(sharedWindow.start), 0) : 0;
  const bandEnd = sharedWindow ? Math.min(pct(sharedWindow.end ?? now), 100) : 0;
  const bandWidth = Math.max(bandEnd - bandStart, 0);
  const sharedDays = sharedWindow
    ? Math.max(1, Math.round(((sharedWindow.end ?? now) - sharedWindow.start) / 86_400_000))
    : 0;

  return (
    <div
      data-tour="combination-window"
      className={cn(
        'rounded-card space-y-3 border p-3',
        sharedWindow ? 'border-sel-line bg-panel2' : 'border-line border-dashed',
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        {sharedWindow ? (
          <>
            <span className="flex items-baseline gap-2 text-body font-medium">
              Shared run window
              <span className="micro-label border-sel-line text-sel rounded-pill border px-2 py-0.5">
                {sharedDays} {sharedDays === 1 ? 'day' : 'days'}
              </span>
            </span>
            <span className="supporting">
              {formatDate(sharedWindow.start)} → {sharedWindow.end ? formatDate(sharedWindow.end) : 'ongoing'}
              {shared.length ? ` · ${shared.join(', ')}` : ''}
            </span>
          </>
        ) : (
          <>
            <span className="text-body font-medium">No shared run window</span>
            <span className="supporting">These experiments never ran at the same time</span>
          </>
        )}
      </div>

      <div className="flex gap-2 py-1">
        <div className="w-28 shrink-0 space-y-1.5">
          {selected.map((experiment) => (
            <div key={experiment.id} className="flex h-2.5 items-center">
              <span className="supporting truncate" title={experiment.name}>
                {experiment.name}
              </span>
            </div>
          ))}
        </div>

        {/*
          The window is the point of the chart, so it is the emphasised mark: a
          filled band with hard edges, while the parts of each run outside it
          recede. Every selected experiment covers the window by definition, it
          is the intersection of their runs, so each bar's bright segment sits
          at exactly the band's coordinates.
        */}
        <div className="relative flex-1">
          {sharedWindow && bandWidth > 0 && (
            <div
              aria-hidden
              className="border-sel-line bg-sel-bg pointer-events-none absolute inset-y-0 border-x-2"
              style={{ left: `${bandStart}%`, width: `${bandWidth}%` }}
            />
          )}

          <div className="relative space-y-1.5">
            {selected.map((experiment) => {
              const { left, width } = barGeometry(experiment, span, now);
              const litStart = Math.max(left, bandStart);
              const litWidth = Math.max(Math.min(left + width, bandEnd) - litStart, 0);

              return (
                <span key={experiment.id} className="bg-track rounded-pill relative block h-2.5">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className="bg-bar-muted rounded-pill absolute inset-y-0"
                        style={{ left: `${left}%`, width: `${width}%` }}
                      />
                    </TooltipTrigger>
                    <TooltipContent>
                      {experiment.name}
                      <br />
                      {runLabel(experiment)}
                    </TooltipContent>
                  </Tooltip>
                  {litWidth > 0 && (
                    <span
                      aria-hidden
                      className="bg-sel pointer-events-none absolute inset-y-0"
                      style={{ left: `${litStart}%`, width: `${litWidth}%` }}
                    />
                  )}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      <div className="micro-label flex items-center justify-between">
        <span className="pl-[7.5rem] tracking-normal">{formatDate(span.min)}</span>
        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1">
            <span className="border-sel-line bg-sel-bg h-2.5 w-3 border-x-2" />
            shared
          </span>
          <span className="flex items-center gap-1">
            <span className="bg-bar-muted rounded-pill h-2.5 w-3" />
            not shared
          </span>
        </span>
        <span className="tracking-normal">{formatDate(span.max)}</span>
      </div>
    </div>
  );
};
